/**
 * Planning page (T16.3 / ADR-0029 / ISSUE-077).
 *
 * Week view → day plan → build-rest-of-today → grocery list.
 * Premium-gated: free users see a teaser + PremiumGate.
 *
 * ED-safety: additive-only framing, no "must eat" / restriction language.
 * Positioning: plans are optional suggestions, not mandates.
 */
import type { GroceryItem, PlanTemplateListItem } from '@nutians/shared/schemas';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumGate } from '../components/PremiumGate.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import {
  useApplyTemplate,
  useDayPlan,
  useDeleteDayPlan,
  useGroceryList,
  usePatchPlanItem,
  usePlanFill,
  usePlanTemplates,
} from '../hooks/usePlanning.js';

// ── Date helpers ──────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Returns Monday of the ISO week containing `isoDate`. */
function weekMonday(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  const dow = d.getUTCDay(); // 0=Sun
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

function addDays(isoDate: string, n: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatDayLabel(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00Z`).getUTCDate().toString();
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

// ── Slot helpers ──────────────────────────────────────────────────────────────

type SlotKey = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'other';
const SLOT_ORDER: SlotKey[] = ['breakfast', 'lunch', 'dinner', 'snack', 'other'];

// ── Grocery list panel ────────────────────────────────────────────────────────

interface GroceryPanelProps {
  from: string;
  to: string;
  onClose: () => void;
}

function GroceryPanel({ from, to, onClose }: GroceryPanelProps) {
  const { t } = useTranslation('planning');
  const { data, isLoading } = useGroceryList(from, to);
  const [checked, setChecked] = useState<Set<number>>(new Set());

  function toggle(i: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  function shareAsText(items: GroceryItem[]) {
    const lines = items
      .map((item, i) => {
        const prefix = checked.has(i) ? '✓ ' : '';
        if (item.quantity !== null && item.unit) {
          return `${prefix}${t('grocery.shareItemWithQty', { name: item.name, quantity: item.quantity, unit: item.unit })}`;
        }
        return `${prefix}${t('grocery.shareItem', { name: item.name })}`;
      })
      .join('\n');
    const text = t('grocery.shareText', { items: lines });
    if (navigator.share) {
      void navigator.share({ title: t('grocery.shareSubject'), text });
    } else {
      void navigator.clipboard.writeText(text);
    }
  }

  const items = data?.items ?? [];

  return (
    <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="text-base font-semibold text-ink">{t('grocery.title')}</p>
          <p className="mt-0.5 text-sm text-ink-soft">{t('grocery.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close grocery list"
          className="text-ink-soft hover:text-ink"
        >
          ✕
        </button>
      </div>

      {isLoading && <p className="text-sm text-ink-soft">Loading…</p>}

      {!isLoading && items.length === 0 && (
        <p className="text-sm text-ink-soft">{t('grocery.empty')}</p>
      )}

      {!isLoading && items.length > 0 && (
        <>
          <ul className="mb-4 divide-y divide-husk">
            {items.map((item, i) => (
              <li
                key={`${item.name}::${item.unit ?? 'none'}`}
                className="flex items-center gap-3 py-2"
              >
                <input
                  type="checkbox"
                  checked={checked.has(i)}
                  onChange={() => toggle(i)}
                  className="h-4 w-4 rounded accent-leaf"
                  aria-label={item.name}
                />
                <span
                  className={`flex-1 text-sm ${checked.has(i) ? 'text-ink-soft line-through' : 'text-ink'}`}
                >
                  {item.name}
                  {item.quantity !== null && item.unit && (
                    <span className="ml-1 text-xs text-ink-soft">
                      {item.quantity} {item.unit}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => shareAsText(items)}
            className="text-sm font-medium text-leaf hover:text-leaf-deep"
          >
            {t('grocery.share')}
          </button>
        </>
      )}
    </div>
  );
}

// ── Template picker ───────────────────────────────────────────────────────────

interface TemplatePickerProps {
  templates: PlanTemplateListItem[];
  weekMonday: string;
  onClose: () => void;
}

function TemplatePicker({ templates, weekMonday: weekStart, onClose }: TemplatePickerProps) {
  const { t } = useTranslation('planning');
  const applyMutation = useApplyTemplate();
  const [selectedId, setSelectedId] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function apply() {
    if (!selectedId) return;
    try {
      await applyMutation.mutateAsync({
        templateId: selectedId,
        weekStartDate: weekStart,
        overwrite,
      });
      setNotice(t('template.applied'));
      setTimeout(onClose, 1200);
    } catch {
      setNotice(t('template.error'));
    }
  }

  if (templates.length === 0) {
    return (
      <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
        <p className="text-sm text-ink-soft">{t('template.noTemplates')}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-3 text-xs text-ink-soft hover:text-ink"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-husk bg-paper px-5 py-5">
      <p className="mb-3 text-base font-semibold text-ink">{t('template.applyTitle')}</p>

      {notice && (
        <div className="mb-3 rounded-xl border border-leaf/30 bg-leaf/10 px-3 py-2 text-sm text-ink">
          {notice}
        </div>
      )}

      <select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        className="mb-3 w-full rounded-xl border border-husk bg-shell px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-leaf"
      >
        <option value="">{t('template.select')}</option>
        {templates.map((tmpl) => (
          <option key={tmpl.id} value={tmpl.id}>
            {tmpl.name}
          </option>
        ))}
      </select>

      <label className="mb-4 flex items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
          className="h-4 w-4 rounded accent-leaf"
        />
        {t('template.overwrite')}
      </label>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={apply}
          disabled={!selectedId || applyMutation.isPending}
          className="rounded-xl bg-leaf px-5 py-2.5 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-60"
        >
          {applyMutation.isPending ? t('template.applying') : t('template.apply')}
        </button>
        <button type="button" onClick={onClose} className="text-sm text-ink-soft hover:text-ink">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Day plan panel ────────────────────────────────────────────────────────────

interface DayPlanPanelProps {
  date: string;
  isToday: boolean;
}

function DayPlanPanel({ date, isToday }: DayPlanPanelProps) {
  const { t } = useTranslation('planning');
  const { data, isLoading } = useDayPlan(date);
  const fillMutation = usePlanFill();
  const patchItem = usePatchPlanItem();
  const deletePlan = useDeleteDayPlan();

  const [fillNotice, setFillNotice] = useState<string | null>(null);

  const plan = data?.plan ?? null;
  const items = plan?.items ?? [];

  const bySlot = SLOT_ORDER.reduce<Record<string, typeof items>>((acc, slot) => {
    acc[slot] = items.filter((it) => it.mealSlot === slot);
    return acc;
  }, {});

  const hasItems = items.length > 0;

  async function runFill(mode: 'deterministic' | 'ai') {
    setFillNotice(null);
    try {
      const result = await fillMutation.mutateAsync({ date, mode });
      const count = result.suggestions.length;
      if (count === 0) {
        setFillNotice(t('fill.noGap'));
      } else {
        setFillNotice(t('fill.applied', { count }));
      }
    } catch (err) {
      const e = err as { code?: string; message?: string } | undefined;
      if (e?.code === 'BatteryEmpty' || e?.message === 'BatteryEmpty') {
        setFillNotice(t('fill.batteryEmpty'));
      } else {
        setFillNotice(t('fill.error'));
      }
    }
  }

  async function toggleLogged(itemId: string, current: boolean) {
    await patchItem.mutateAsync({ date, itemId, isLogged: !current });
  }

  async function clearPlan() {
    if (!plan) return;
    if (!window.confirm(t('day.confirmClear'))) return;
    await deletePlan.mutateAsync(date);
  }

  if (isLoading) return <p className="text-sm text-ink-soft">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      {/* Fill CTA */}
      {isToday && (
        <div className="rounded-2xl border border-husk bg-paper px-5 py-4">
          <p className="mb-1 text-sm font-medium text-ink">{t('fill.title')}</p>
          <p className="mb-3 text-xs text-ink-soft">{t('fill.subtitle')}</p>

          {fillNotice && (
            <p className="mb-3 text-sm text-ink" role="status">
              {fillNotice}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runFill('deterministic')}
              disabled={fillMutation.isPending}
              className="rounded-xl bg-leaf px-4 py-2 text-sm font-semibold text-white hover:bg-leaf-deep disabled:opacity-60"
            >
              {fillMutation.isPending && fillMutation.variables?.mode !== 'ai'
                ? t('fill.running')
                : t('fill.runDeterministic')}
            </button>
            <button
              type="button"
              onClick={() => runFill('ai')}
              disabled={fillMutation.isPending}
              className="rounded-xl border border-leaf px-4 py-2 text-sm font-medium text-leaf hover:bg-leaf/10 disabled:opacity-60"
            >
              {fillMutation.isPending && fillMutation.variables?.mode === 'ai'
                ? t('fill.running')
                : `${t('fill.runAi')} · ${t('fill.aiNote')}`}
            </button>
          </div>
        </div>
      )}

      {/* Plan items grouped by slot */}
      {hasItems ? (
        <div className="flex flex-col gap-3">
          {SLOT_ORDER.map((slot) => {
            const slotItems = bySlot[slot] ?? [];
            if (slotItems.length === 0) return null;
            return (
              <div key={slot}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft">
                  {t(`slot.${slot}`)}
                </p>
                <div className="flex flex-col gap-1.5">
                  {slotItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-xl border border-husk bg-white px-3 py-2"
                    >
                      <button
                        type="button"
                        onClick={() => toggleLogged(item.id, item.isLogged)}
                        aria-label={item.isLogged ? t('item.markUnlogged') : t('item.markLogged')}
                        className={`h-5 w-5 shrink-0 rounded-full border-2 transition-colors ${
                          item.isLogged
                            ? 'border-leaf bg-leaf'
                            : 'border-husk bg-white hover:border-leaf'
                        }`}
                      >
                        {item.isLogged && (
                          <span className="flex items-center justify-center text-[10px] font-bold text-white">
                            ✓
                          </span>
                        )}
                      </button>
                      <span
                        className={`flex-1 text-sm ${item.isLogged ? 'text-ink-soft line-through' : 'text-ink'}`}
                      >
                        {item.label ?? t('item.logged')}
                      </span>
                      {item.isLogged && (
                        <span className="text-xs text-leaf">{t('item.logged')}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={clearPlan}
            className="self-start text-xs text-ink-soft hover:text-clay"
          >
            {t('day.clearPlan')}
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-husk px-4 py-6 text-center">
          <p className="text-sm text-ink-soft">{t('day.empty')}</p>
          <p className="mt-1 text-xs text-ink-soft">{t('day.emptyHint')}</p>
        </div>
      )}
    </div>
  );
}

// ── Week cell ─────────────────────────────────────────────────────────────────

interface WeekCellProps {
  date: string;
  dayKey: string;
  isToday: boolean;
  isSelected: boolean;
  onClick: () => void;
}

function WeekCell({ date, dayKey, isToday, isSelected, onClick }: WeekCellProps) {
  const { t } = useTranslation('planning');
  const { data } = useDayPlan(date);
  const itemCount = data?.plan?.items.length ?? 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 flex-col items-center rounded-xl border px-2 py-2.5 transition-colors ${
        isSelected
          ? 'border-leaf bg-leaf/10 text-leaf'
          : isToday
            ? 'border-leaf/40 bg-leaf/5 text-ink'
            : 'border-husk bg-white text-ink hover:border-leaf/40'
      }`}
    >
      <span className="text-xs font-medium uppercase tracking-wide">
        {t(`week.${dayKey as 'mon'}`)}
      </span>
      <span className={`mt-0.5 text-lg font-semibold ${isToday ? 'text-leaf' : ''}`}>
        {formatDayLabel(date)}
      </span>
      {itemCount > 0 && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-leaf" aria-hidden />}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PlanningPage() {
  const { t } = useTranslation('planning');
  const { data: entitlement } = useEntitlement();
  const isPremium = entitlement?.isPremium ?? false;

  const today = todayIso();
  const [currentMonday, setCurrentMonday] = useState(() => weekMonday(today));
  const [selectedDate, setSelectedDate] = useState(today);
  const [showGrocery, setShowGrocery] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const { data: templateData } = usePlanTemplates();
  const templates = templateData?.templates ?? [];

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(currentMonday, i));
  const weekEnd = weekDates[6] ?? currentMonday;

  function prevWeek() {
    setCurrentMonday((m) => addDays(m, -7));
  }

  function nextWeek() {
    setCurrentMonday((m) => addDays(m, 7));
  }

  const isCurrentWeek = currentMonday === weekMonday(today);

  if (!isPremium) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-medium tracking-tight">{t('page.title')}</h2>
          <p className="mt-1 text-sm leading-6 text-ink-soft">{t('page.subtitle')}</p>
        </div>
        <PremiumGate feature="planning" heading={t('upsell.heading')} body={t('upsell.body')} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-medium tracking-tight">{t('page.title')}</h2>
          <p className="mt-1 text-sm leading-6 text-ink-soft">{t('page.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowTemplatePicker((v) => !v)}
            className="rounded-xl border border-husk px-3 py-1.5 text-sm text-ink hover:border-leaf hover:text-leaf"
          >
            {t('template.apply')}
          </button>
          <button
            type="button"
            onClick={() => setShowGrocery((v) => !v)}
            className="rounded-xl border border-husk px-3 py-1.5 text-sm text-ink hover:border-leaf hover:text-leaf"
          >
            {t('grocery.title')}
          </button>
        </div>
      </div>

      {/* Template picker */}
      {showTemplatePicker && (
        <TemplatePicker
          templates={templates}
          weekMonday={currentMonday}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      {/* Grocery list */}
      {showGrocery && (
        <GroceryPanel from={currentMonday} to={weekEnd} onClose={() => setShowGrocery(false)} />
      )}

      {/* Week navigation */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={prevWeek}
          aria-label={t('week.prev')}
          className="rounded-lg border border-husk px-2 py-1 text-sm text-ink-soft hover:text-ink"
        >
          ‹
        </button>
        <div className="flex flex-1 gap-1">
          {weekDates.map((date, i) => (
            <WeekCell
              key={date}
              date={date}
              dayKey={DAY_KEYS[i] ?? DAY_KEYS[0]}
              isToday={date === today}
              isSelected={date === selectedDate}
              onClick={() => setSelectedDate(date)}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={nextWeek}
          aria-label={t('week.next')}
          className="rounded-lg border border-husk px-2 py-1 text-sm text-ink-soft hover:text-ink"
        >
          ›
        </button>
      </div>

      {/* "Back to today" shortcut */}
      {!isCurrentWeek && (
        <button
          type="button"
          onClick={() => {
            setCurrentMonday(weekMonday(today));
            setSelectedDate(today);
          }}
          className="self-start text-sm text-leaf hover:text-leaf-deep"
        >
          {t('week.today')}
        </button>
      )}

      {/* Selected day plan */}
      <DayPlanPanel date={selectedDate} isToday={selectedDate === today} />
    </div>
  );
}
